import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, BadgeCheck, Building2, Car, Check, Globe, Mail, MapPin, Plane, Send, ShieldCheck,
  Smartphone, UserRound,
} from "lucide-react";
import api, { PLATFORM_NAME, money } from "@/lib/api";
import { LatestPosts } from "@/components/LatestPosts";
import { Button } from "@/components/ui/button";

const HERO = "https://images.unsplash.com/photo-1764090317825-9b76e437c8d8?crop=entropy&cs=srgb&fm=jpg&q=85";
const SUV = "https://images.unsplash.com/photo-1767749995450-7b63ab7cd4fd?crop=entropy&cs=srgb&fm=jpg&q=85";
const CHAUFFEUR = "https://images.pexels.com/photos/36498953/pexels-photo-36498953.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CAPABILITIES = [
  { icon: MapPin, title: "Address intelligence", body: "Type a few letters and pick the exact pickup or drop-off. We store coordinates, place ID and the formatted address." },
  { icon: Send, title: "Send to driver", body: "One click emails the full manifest to the chauffeur, who accepts or declines from their phone." },
  { icon: Plane, title: "Flight aware trips", body: "Airline, flight number and terminal live on every manifest so airport runs never slip." },
  { icon: Building2, title: "Vehicle pricing rules", body: "Base rate plus deposit model per vehicle: percentage split, fixed deposit, full payment or pay after trip." },
  { icon: Globe, title: "Your domain", body: "Run dispatch and booking on book.yourcompany.com with SSL on branded plans." },
  { icon: Mail, title: "Your email", body: "Customer-facing email under your own sender domain with your templates." },
];

const TERMS = [
  { slug: "weekly", label: "Pay weekly", off: "no commitment" },
  { slug: "quarterly", label: "3 months up front", off: "10% off" },
  { slug: "semiannual", label: "6 months up front", off: "20% off" },
  { slug: "annual", label: "12 months up front", off: "40% off" },
];

const DOORS = [  {
    key: "office", to: "/office", icon: Building2, label: "Office & dispatch",
    who: "Owners, dispatchers and managers",
    body: "Dispatch board, trips, airport manifests, invoices, payouts and 1099s.",
  },
  {
    key: "drivers", to: "/drivers", icon: Car, label: "Chauffeur app",
    who: "Drivers on the road",
    body: "Today's runs, one-tap status, wait timers, passenger chat and earnings.",
  },
  {
    key: "clients", to: "/clients", icon: UserRound, label: "Passenger app",
    who: "Your clients",
    body: "Book a ride, track the car live, message the chauffeur, receipts and tips.",
  },
];

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const [trialWeeks, setTrialWeeks] = useState(2);

  useEffect(() => {
    api.get("/plans").then(({ data }) => {
      setPlans(data.plans);
      if (data.trial_days) setTrialWeeks(Math.round(data.trial_days / 7));
    }).catch(() => setPlans([]));
  }, []);

  // Anchors are scrolled in JS: the native hash jump uses a stale offset while the hero image and
  // the plan cards are still settling, which landed people past pricing on the disclosure section.
  const jumpTo = (id) => (e) => {
    e.preventDefault();
    const target = document.getElementById(id);
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - 64; // fixed header height
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-ink-900 text-slate-200">
      <header className="glass-header fixed inset-x-0 top-0 z-50 border-b border-ink-600">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 font-display text-lg font-semibold tracking-tight">
            <img src="/limolinkx-logo.png" alt="" className="h-9 w-9 rounded bg-white/95 object-contain p-0.5" />
            <span className="gold-text">{PLATFORM_NAME}</span>
          </Link>
          <nav className="hidden items-center gap-8 md:flex">
            <a href="#apps" onClick={jumpTo("apps")} data-testid="nav-apps-link" className="text-sm text-slate-400 transition-colors hover:text-gold">Apps</a>
            <a href="#capabilities" onClick={jumpTo("capabilities")} className="text-sm text-slate-400 transition-colors hover:text-gold">Platform</a>
            <a href="#pricing" onClick={jumpTo("pricing")} data-testid="nav-pricing-link" className="text-sm text-slate-400 transition-colors hover:text-gold">Pricing</a>
            <Link to="/blog" data-testid="nav-blog-link" className="text-sm text-slate-400 transition-colors hover:text-gold">Blog</Link>
            <Link to="/legal/terms" className="text-sm text-slate-400 transition-colors hover:text-gold">Legal</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm" className="text-slate-300 hover:text-gold">
              <Link to="/office" data-testid="header-login-btn">Sign in</Link>
            </Button>
            <Button asChild size="sm" className="bg-gold text-ink-900 hover:bg-gold-light">
              <Link to="/signup" data-testid="header-signup-btn">Start free</Link>
            </Button>
          </div>
        </div>
      </header>

      <section className="relative min-h-[92vh] overflow-hidden pt-16">
        <img src={HERO} alt="Executive black car at night" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(7,9,14,0.55) 0%, rgba(7,9,14,0.92) 70%, #07090E 100%)" }} />
        <div className="relative mx-auto max-w-7xl px-4 pt-24 sm:px-6 lg:px-8 lg:pt-36">
          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.28em] text-gold"
          >
            Black car · Executive sedan · SUV · Limousine
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-6 max-w-3xl text-4xl font-bold leading-[1.05] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl"
          >
            Dispatch-grade manifests for operators who never miss a pickup.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-400"
          >
            Build a branded trip manifest in under a minute, email it to your chauffeur, and know the moment
            they accept. Two trips a week free — no card, no sales call.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-10 flex flex-col gap-3 sm:flex-row"
          >
            <Button asChild size="lg" className="h-12 rounded-full bg-gold px-8 text-ink-900 hover:bg-gold-light">
              <Link to="/signup" data-testid="hero-signup-btn">
                Create free account <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="h-12 rounded-full border-ink-600 bg-transparent px-8 text-slate-200 hover:border-gold/50 hover:text-gold">
              <a href="#pricing" onClick={jumpTo("pricing")} data-testid="hero-pricing-link">Compare plans</a>
            </Button>
          </motion.div>
          <p className="mt-6 font-mono text-xs text-slate-500">2 free trips every week — no payment required.</p>
          <p className="mt-3 font-mono text-xs text-slate-500">
            Already with us?{" "}
            <a href="#apps" onClick={jumpTo("apps")} data-testid="hero-apps-link" className="text-gold underline">Open your app</a>
          </p>
        </div>
      </section>

      <section id="apps" className="scroll-mt-20 border-b border-ink-600 bg-ink-800/40 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.28em] text-gold">
                <Smartphone className="h-3.5 w-3.5" /> Open your app
              </p>
              <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl">
                Three apps, one for each seat in the business.
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Pick yours to sign in — each one installs to your home screen and opens full screen,
                so you only ever see the tools for your job.
              </p>
            </div>
          </div>

          <div data-testid="app-doors" className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
            {DOORS.map((d) => (
              <Link
                key={d.key}
                to={d.to}
                data-testid={`landing-door-${d.key}`}
                className="card-hover group flex flex-col rounded-xl border border-ink-600 bg-ink-800 p-6 transition-colors hover:border-gold/50"
              >
                <d.icon className="h-6 w-6 text-gold" />
                <h3 className="mt-5 text-lg font-medium text-slate-100">{d.label}</h3>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{d.who}</p>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{d.body}</p>
                <span className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
                  Sign in <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>

          <p className="mt-8 text-xs text-slate-500">
            New chauffeurs and passengers are invited by their transportation company — ask them for your
            invite, then sign in above.
          </p>
        </div>
      </section>

      <section id="capabilities" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">The platform</p>
          <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
            Manifest, dispatch, driver, payments — one operating system for your fleet.
          </h2>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {CAPABILITIES.map((c) => (
            <div key={c.title} className="card-hover rounded-xl border border-ink-600 bg-ink-800 p-6">
              <c.icon className="h-5 w-5 text-gold" />
              <h3 className="mt-5 text-lg font-medium text-slate-100">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 pb-20 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div className="overflow-hidden rounded-2xl border border-ink-600">
          <img src={CHAUFFEUR} alt="Chauffeur in suit" className="h-[420px] w-full object-cover" />
        </div>
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Driver workflow</p>
          <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl">
            The chauffeur gets everything, and you get an answer.
          </h2>
          <ul className="mt-8 space-y-4">
            {["Trip, passenger, flight and vehicle detail in one email",
              "Special instructions and operator contact included",
              "ACCEPT JOB / DECLINE JOB from any phone",
              "Manifest status updates the second they respond"].map((t) => (
              <li key={t} className="flex gap-3 text-sm text-slate-300">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {t}
              </li>
            ))}
          </ul>
          <div className="mt-8 overflow-hidden rounded-xl border border-ink-600">
            <img src={SUV} alt="Executive SUV" className="h-48 w-full object-cover" />
          </div>
        </div>
      </section>

      <section id="pricing" className="scroll-mt-20 border-y border-ink-600 bg-ink-800/40 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Pricing</p>
            <h2 className="mt-4 text-2xl font-semibold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
              Two weeks free. Then pay weekly, or pay ahead and pay less.
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              Every paid plan starts with {trialWeeks} free weeks — cancel before they end and you are charged
              nothing. After that, keep it weekly or settle a longer term up front and keep the difference.
            </p>
            <div data-testid="term-discounts" className="mt-6 flex flex-wrap gap-2">
              {TERMS.map((t) => (
                <span key={t.label} data-testid={`term-discount-${t.slug}`}
                  className="rounded-full border border-gold/30 bg-gold/5 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-gold">
                  {t.label} · {t.off}
                </span>
              ))}
            </div>
          </div>
          <div data-testid="pricing-grid" className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {plans.map((p) => (
              <div
                key={p.key}
                data-testid={`plan-card-${p.key}`}
                className={`flex flex-col rounded-xl border p-6 ${
                  p.key === "professional" ? "border-gold/50 bg-ink-700" : "border-ink-600 bg-ink-800"
                }`}
              >
                {p.key === "professional" && (
                  <span className="mb-3 inline-flex w-fit items-center rounded-full bg-gold/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-gold">
                    Most popular
                  </span>
                )}
                <h3 className="text-lg font-medium text-slate-100">{p.name}</h3>
                <p className="mt-3 font-display text-3xl font-bold text-slate-50">
                  {p.price === 0 ? "$0" : money(p.price).replace(".00", "")}
                  <span className="ml-1 text-xs font-normal text-slate-500">/week</span>
                </p>
                {p.price > 0 && (
                  <p data-testid={`plan-trial-${p.key}`} className="mt-1 font-mono text-[11px] text-emerald-300">
                    {trialWeeks} weeks free, then cancel or carry on
                  </p>
                )}
                <p className="mt-2 min-h-[40px] text-xs leading-relaxed text-slate-500">{p.tagline}</p>
                <p className="mt-3 font-mono text-[11px] text-gold">
                  {p.manifest_limit ? `${p.manifest_limit} trips a week` : "Unlimited trips"}
                </p>
                <ul className="mt-5 flex-1 space-y-2.5">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2 text-xs leading-relaxed text-slate-400">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  className={`mt-6 w-full ${
                    p.key === "free"
                      ? "bg-gold text-ink-900 hover:bg-gold-light"
                      : "border border-ink-600 bg-transparent text-slate-200 hover:border-gold/50 hover:text-gold"
                  }`}
                >
                  <Link to="/signup" data-testid={`plan-cta-${p.key}`}>
                    {p.key === "free" ? "Start free" : "Get started"}
                  </Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="rounded-xl border border-ink-600 bg-ink-800 p-6 md:p-8">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-gold" />
            <h3 className="text-lg font-medium text-slate-100">Software Provider Disclosure</h3>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">
            {PLATFORM_NAME} is a software technology provider. We provide transportation management, dispatch,
            communication, booking, payment integration, GPS and related software tools. We do not provide
            transportation services, operate vehicles, employ chauffeurs on behalf of transportation operators,
            sell transportation services, or act as a transportation carrier. Transportation operators are solely
            responsible for their vehicles, chauffeurs, customers, reservations, pricing, transportation services,
            permits, licenses, insurance, taxes, compliance and fulfilment of trips. Any transportation agreement
            is between the transportation operator and its customer; {PLATFORM_NAME} is not a party to that
            agreement.
          </p>
          <Link to="/legal/terms" data-testid="legal-terms-link" className="mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-gold">
            Read the full terms <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>

      <LatestPosts />

      <footer className="border-t border-ink-600 py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 sm:flex-row sm:px-6 lg:px-8">
          <p className="font-display text-sm text-slate-400">
            <span className="gold-text font-semibold">{PLATFORM_NAME}</span> · transportation management software
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-mono text-xs text-slate-500">
            <Link to="/office" data-testid="footer-door-office" className="hover:text-gold">Office</Link>
            <Link to="/drivers" data-testid="footer-door-drivers" className="hover:text-gold">Chauffeurs</Link>
            <Link to="/clients" data-testid="footer-door-clients" className="hover:text-gold">Passengers</Link>
            <Link to="/blog" data-testid="footer-blog-link" className="hover:text-gold">Blog</Link>
            <Link to="/legal/terms" className="hover:text-gold">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-gold">Privacy</Link>
            <Link to="/legal/sms" className="hover:text-gold">SMS terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
